import * as React from 'react';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, TextField } from '@mui/material';
import {
  Mic,
  Video,
  Settings,
  Timer,
  Folder,
  Keyboard,
  Bell,
  Monitor,
  Tv,
  Gauge,
  Play,
  Video as VideoIcon,
  MonitorCog,
} from 'lucide-mui';
import { RootState } from '../../reducers';
import { updateSettings, NewSettings } from './settingsSlice';
import {
  ToggleSetting,
  SettingsSection,
  recordingSubSettings,
  SubSettings,
} from './common';
import { RecorderSettings } from '../../types/state';
import { formatString } from './utils';

const RecordingSettings = () => {
  const dispatch = useDispatch();
  const recorder = useSelector((state: RootState) => state.settings.recorder);
  const [modifiedSettings, setModifiedSettings] = useState<RecorderSettings>(
    recorder
  );

  useEffect(() => {
    setModifiedSettings(recorder);
  }, [recorder]);

  const handleChange = (
    subSetting: SubSettings,
    setting: string,
    newValue: boolean | string
  ) => {
    setModifiedSettings((prevSettings) => ({
      ...prevSettings,
      [subSetting]: {
        ...prevSettings[subSetting],
        [setting]: newValue,
      },
    }));
  };

  const handleSave = () => {
    dispatch(
      updateSettings({
        key: 'recorder',
        newSettings: modifiedSettings as NewSettings,
      })
    );
  };

  const getIconForSetting = (section: string, setting: string) => {
    // First check specific settings
    switch (setting) {
      case 'enableAudio':
        return <Mic />;
      case 'audioDevice':
        return <Monitor />;
      case 'enableVideo':
        return <Video />;
      case 'videoDevice':
        return <Monitor />;
      case 'resolution':
        return <Tv />;
      case 'frameRate':
        return <Gauge />;
      case 'autoStart':
        return <Play />;
      case 'countdownTimer':
        return <Timer />;
      case 'recordingFormat':
        return <VideoIcon />;
      case 'outputDirectory':
        return <Folder />;
      case 'hotkeys':
        return <Keyboard />;
      case 'notifications':
        return <Bell />;
      default:
        // Fallback to section icons if no specific icon
        switch (section) {
          case 'audioSettings':
            return <Mic />;
          case 'videoSettings':
            return <Video />;
          case 'recordingControlSettings':
            return <MonitorCog />;
          default:
            return <Settings />;
        }
    }
  };

  const getSettingDescription = (
    setting: string,
    isToggle: boolean
  ): string => {
    // Helper function to format camelCase to readable text
    const formatSettingName = (text: string): string => {
      return (
        text
          // Split camelCase
          .replace(/([A-Z])/g, ' $1')
          // Remove any special characters and extra spaces
          .replace(/[^a-zA-Z0-9 ]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim()
          // Capitalize first letter of each word
          .split(' ')
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(' ')
      );
    };

    if (isToggle) {
      const toggleDescriptions: { [key: string]: string } = {
        enableAudio: 'Record audio during screen captures',
        enableVideo: 'Include video in your recordings',
        autoStart: 'Start recording automatically when session begins',
        notifications: 'Show notifications during recording sessions',
        browserAudio: 'Capture audio from browser tabs',
        systemAudio: 'Record system sounds and audio',
        microphoneAudio: 'Use microphone for voice recording',
        webcamVideo: 'Include webcam footage in recording',
        showCursor: 'Display cursor movements in recording',
        highlightClicks: 'Highlight mouse clicks in recording',
      };
      return (
        toggleDescriptions[setting] || `Enable ${formatSettingName(setting)}`
      );
    }

    const inputDescriptions: { [key: string]: string } = {
      audioDevice: 'Choose which microphone or audio input to use',
      videoDevice: 'Select your preferred camera or video source',
      resolution: 'Choose the quality and dimensions of your recording',
      frameRate: 'Set how many frames per second to capture',
      countdownTimer: 'How many seconds to wait before starting',
      recordingFormat: 'Select the output file format for recordings',
      outputDirectory: 'Where to save your recorded files',
      hotkeys: 'Customize your keyboard shortcuts',
      maxDuration: 'Set maximum recording duration',
      bufferSize: 'Adjust recording buffer size',
      audioQuality: 'Set the audio recording quality',
      videoQuality: 'Choose video recording quality',
    };
    return inputDescriptions[setting] || `Choose ${formatSettingName(setting)}`;
  };

  const renderTextField = (
    setting: string,
    value: string,
    onChange: (newValue: string) => void
  ) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', sm: 'flex-end' },
        flex: '0 0 auto',
        width: { xs: '100%', sm: '200px' },
        mt: { xs: 2, sm: 0 },
      }}
    >
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
      />
    </Box>
  );

  return (
    <Box
      sx={{
        flexGrow: 1,
        height: '100%',
        overflow: 'auto',
        bgcolor: 'transparent',
        px: 3,
        pt: 1,
        pb: 3,
        '& > *:not(:last-child)': {
          mb: 3,
        },
      }}
    >
      {recordingSubSettings.map((subSetting) => (
        <SettingsSection key={subSetting} title={formatString(subSetting)}>
          {Object.entries(modifiedSettings[subSetting] || {}).map(
            ([setting, value]) => {
              // Skip defaultTranscriptionLanguage in otherSettings
              if (
                subSetting === 'otherSettings' &&
                setting === 'defaultTranscriptionLanguage'
              ) {
                return null;
              }
              const isToggle = typeof value === 'boolean';
              return (
                <ToggleSetting
                  key={setting}
                  checked={isToggle ? (value as boolean) : false}
                  onChange={(e) =>
                    handleChange(
                      subSetting,
                      setting,
                      isToggle ? e.target.checked : e.target.value
                    )
                  }
                  label={formatString(setting)}
                  description={getSettingDescription(setting, isToggle)}
                  icon={getIconForSetting(subSetting, setting)}
                  rightElement={
                    !isToggle
                      ? renderTextField(
                          setting,
                          value as string,
                          (newValue: string) =>
                            handleChange(subSetting, setting, newValue)
                        )
                      : undefined
                  }
                />
              );
            }
          )}
        </SettingsSection>
      ))}

      <Button
        id="recording-settings-save-button"
        variant="contained"
        color="primary"
        onClick={handleSave}
        sx={{ marginTop: 2, minWidth: 120 }}
      >
        Save Changes
      </Button>
    </Box>
  );
};

export default RecordingSettings;
